import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import AnimateHeight from "react-animate-height";
import Modal from "./Modal";
import Button from "./Button";
import LikertScale from "./LikertScale";
import PercentageScale from "./PercentageScale";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import i18next from "i18next";

/**
 * @extends {Component<PropTypes.InferProps<typeof ModalQuestion.propTypes>>}
 */
class ModalQuestion extends Component {
    static propTypes = {
        image: PropTypes.string,
        title: PropTypes.string,
        member: PropTypes.string,
        memberFor: PropTypes.string,
        color: PropTypes.string,
        text_color: PropTypes.string,
        question: PropTypes.string,
        additionalGuidingQuestion: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.arrayOf(
                PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            ),
            PropTypes.object,
        ]),
        additionalGuidingQuestions: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.arrayOf(
                PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            ),
            PropTypes.object,
        ]),
        guidingQuestion1: PropTypes.string,
        guidingQuestion2: PropTypes.string,
        guidingQuestions: PropTypes.arrayOf(PropTypes.string),
        headerColor: PropTypes.string,
        headerBg: PropTypes.string,
        submitButton: PropTypes.shape(Button.propTypes),

        // Time in minute
        time: PropTypes.number,
        isOpen: PropTypes.bool,
        hasMedia: PropTypes.bool,
        introType: PropTypes.string,
        onRewatchMedia: PropTypes.func,
        hasScale: PropTypes.bool,
        scaleType: PropTypes.string,
        scaleLabels: PropTypes.object,
        onLikertChange: PropTypes.func,
        onPercentageChange: PropTypes.func,
        onSubmitScaleAnswer: PropTypes.func,
        isRewatch: PropTypes.bool,
    };

    static defaultProps = {
        isOpen: false,
        hasMedia: false,
        additionalGuidingQuestion: "",
        additionalGuidingQuestions: null,
        guidingQuestion1: "",
        guidingQuestion2: "",
        guidingQuestions: [],
        introType: "media_file",
        onRewatchMedia: () => {},
        hasScale: false,
        scaleType: "likert",
        scaleLabels: null,
        onLikertChange: () => {},
        onPercentageChange: () => {},
        onSubmitScaleAnswer: (data) => {},
        isRewatch: false,
    };

    constructor(props) {
        super(props);
        this.state = {
            timeLeft: props.time,
            timer: null,
            isOpenTeamAction: false,
            isOpenGuidingQuestions: false,
            teamAction: "",
            teamActions: [],
            isSubmitted: false,
            isMaxChar: false,
            isDisabled: false,
            likertValue: 3,
            percentageValue: 50,
            popup_text: window.team_action.team_action_popup_text,
            addTeamActionText: window.team_action.team_action_add_button_text
                ? window.team_action.team_action_add_button_text
                : "Add",
            editTeamActionText: window.team_action.team_action_edit_button_text
                ? window.team_action.team_action_edit_button_text
                : "Edit",
        };

        this.teamActionField = createRef();
        this.isRewatching = false;
        this.isPausedForRewatch = false;
        this.timerId = null;
        this.currentTimeLeft =
            typeof props.time === "number" ? props.time : null;
    }

    componentDidUpdate(prevProps, prevState) {
        const isQuestionChanged = prevProps.question !== this.props.question;

        if (isQuestionChanged) {
            this.isRewatching = false;
            this.isPausedForRewatch = false;
            this.currentTimeLeft = this.props.time;
        }

        if (!prevProps.isOpen && this.props.isOpen) {
            const isRewatch =
                !isQuestionChanged &&
                (this.isRewatching ||
                    this.props.isRewatch ||
                    this.isPausedForRewatch);

            this.isRewatching = false;
            this.isPausedForRewatch = false;

            if (isRewatch) {
                this.resumeTimer();
                setTimeout(() => {
                    this.adjustTextareaHeight();
                }, 50);
            } else {
                // Opening Modal, before its get rendered
                this.setState({
                    likertValue: 3,
                    percentageValue: 50,
                    isOpenGuidingQuestions: false,
                    teamAction: "",
                    teamActions: [],
                    isSubmitted: false,
                    isMaxChar: false,
                });
                this.startTimer();

                // Seed the default scale answer so an untouched slider still
                // counts as an answer (matches what the slider visually shows).
                if (this.props.hasScale) {
                    const defaultAnswer =
                        this.props.scaleType === "percentage" ? 50 : 3;
                    this.props.onSubmitScaleAnswer?.({ answer: defaultAnswer });
                }
            }
        } else if (prevProps.isOpen && !this.props.isOpen) {
            //  Closing Modal, before its get rendered
            this.pauseTimer();
        } else if (isQuestionChanged) {
            this.stopTimer();
            this.currentTimeLeft = this.props.time;
            this.setState({
                likertValue: 3,
                percentageValue: 50,
                isOpenGuidingQuestions: false,
                teamAction: "",
                teamActions: [],
                isSubmitted: false,
                isMaxChar: false,
                timeLeft: this.props.time,
            });
        }
    }

    componentWillUnmount() {
        this.stopTimer();
    }

    // componentDidMount() {
    //   const customFontsHeading = new FontFace(
    //     "Custom Fonts Heading",
    //     `url(${this.state.font_heading})`
    //   );
    //   const customFontsBody = new FontFace(
    //     "Custom Fonts Heading",
    //     `url(${this.state.font_body})`
    //   );

    //   customFontsHeading.load().then(() => {
    //     document.fonts.add(customFontsHeading);
    //   });

    //   customFontsBody.load().then(() => {
    //     document.fonts.add(customFontsBody);
    //   });
    // }

    startTimer = () => {
        this.stopTimer();
        const initialTime =
            typeof this.props.time === "number" && this.props.time > 0
                ? this.props.time
                : 60;
        this.currentTimeLeft = initialTime;
        this.setState({ timeLeft: initialTime });

        this.timerId = setInterval(() => {
            if (
                typeof this.currentTimeLeft === "number" &&
                this.currentTimeLeft > 0
            ) {
                this.currentTimeLeft -= 1;
                this.setState({ timeLeft: this.currentTimeLeft });
            } else {
                this.stopTimer();
            }
        }, 1000);
    };

    pauseTimer = () => {
        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
        }
    };

    resumeTimer = () => {
        this.pauseTimer();

        if (
            typeof this.currentTimeLeft !== "number" ||
            isNaN(this.currentTimeLeft)
        ) {
            this.currentTimeLeft =
                typeof this.state.timeLeft === "number"
                    ? this.state.timeLeft
                    : this.props.time;
        }

        this.setState({ timeLeft: this.currentTimeLeft });

        if (this.currentTimeLeft > 0) {
            this.timerId = setInterval(() => {
                if (
                    typeof this.currentTimeLeft === "number" &&
                    this.currentTimeLeft > 0
                ) {
                    this.currentTimeLeft -= 1;
                    this.setState({ timeLeft: this.currentTimeLeft });
                } else {
                    this.stopTimer();
                }
            }, 1000);
        }
    };

    stopTimer = () => {
        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
        }
    };

    setTimer = (resume = false) => {
        if (resume) {
            this.resumeTimer();
        } else {
            this.startTimer();
        }
    };

    resetTimer = () => {
        this.stopTimer();
    };

    adjustTextareaHeight = () => {
        if (this.teamActionField && this.teamActionField.current) {
            const el = this.teamActionField.current;
            el.style.height = "auto";
            el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
        }
    };

    inputTeamAction = (e) => {
        const val = e.target.value;
        this.setState(
            {
                teamAction: val,
            },
            () => {
                this.adjustTextareaHeight();
            },
        );

        if (e.target.value.length >= 950) {
            this.setState({ isMaxChar: true });
            if (typeof this.props.isMaxChar === "function") {
                this.props.isMaxChar();
            }
        } else {
            this.setState({ isMaxChar: false });
        }
    };

    handleEdit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        this.setState({ isSubmitted: false }, () => {
            this.adjustTextareaHeight();
            if (this.teamActionField && this.teamActionField.current) {
                this.teamActionField.current.focus();
            }
        });
    };

    handleAddNewTeamAction = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (this.state.teamAction && this.state.teamAction.trim().length > 0) {
            this.setState(
                (prevState) => ({
                    teamActions: [
                        ...prevState.teamActions,
                        prevState.teamAction.trim(),
                    ],
                    teamAction: "",
                    isSubmitted: false,
                    isMaxChar: false,
                }),
                () => {
                    if (this.teamActionField && this.teamActionField.current) {
                        this.teamActionField.current.style.height = "auto";
                        this.teamActionField.current.focus();
                    }
                },
            );
        }
    };

    onSubmitTeamAction = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (this.state.teamAction && this.state.teamAction.trim().length > 0) {
            this.setState({ isSubmitted: true });
        }
    };

    handleRewatchMedia = () => {
        this.isRewatching = true;
        this.isPausedForRewatch = true;
        this.pauseTimer();
        if (this.props.onRewatchMedia) {
            this.props.onRewatchMedia();
        }
    };

    handleClose = () => {
        // Make sure the current scale value is persisted even if the user
        // never dragged the slider (onChange would not have fired).
        if (this.props.hasScale) {
            const scaleAnswer =
                this.props.scaleType === "percentage"
                    ? this.state.percentageValue
                    : this.state.likertValue;
            this.props.onSubmitScaleAnswer?.({ answer: scaleAnswer });
        }

        const allTeamActions = [...this.state.teamActions];
        if (this.state.teamAction && this.state.teamAction.trim().length > 0) {
            allTeamActions.push(this.state.teamAction.trim());
        }

        this.props.onSubmitTeamAction(allTeamActions);
        this.stopTimer();
        this.currentTimeLeft = this.props.time;
        this.isRewatching = false;
        this.isPausedForRewatch = false;
        this.setState({
            teamAction: "",
            teamActions: [],
            isSubmitted: false,
            isOpenTeamAction: false,
            isMaxChar: false,
            likertValue: 3,
            percentageValue: 50,
            isOpenGuidingQuestions: false,
            timeLeft: this.props.time,
        });
        if (this.teamActionField && this.teamActionField.current) {
            this.teamActionField.current.style.height = "auto";
        }
    };

    toggleTeamAction = () => {
        this.setState({ isOpenTeamAction: !this.state.isOpenTeamAction });
    };

    toggleGuidingQuestions = () => {
        this.setState((prevState) => ({
            isOpenGuidingQuestions: !prevState.isOpenGuidingQuestions,
        }));
    };

    render() {
        const twoDigit = (number) => (number < 10 ? `0${number}` : number);

        const timeLeft = this.state.timeLeft;
        const minute = Math.floor(timeLeft / 60);
        const second = timeLeft - 60 * minute;
        const minuteLeft = twoDigit(minute);
        const secondLeft = twoDigit(second);
        const isLowTime = typeof timeLeft === "number" && timeLeft <= 20;

        const hangerUrl = `/wp-content/themes/teamconnector2/assets/images/hanger.png`;

        const currentActionNumber =
            this.state.teamActions.length + (this.state.isSubmitted ? 1 : 0);
        const addedFeedbackText =
            currentActionNumber > 1
                ? `Team action added ${currentActionNumber}`
                : window.setup_team?.setup_team_member_added_feedback_text ||
                  "Added";

        return (
            <Modal
                className="question"
                isOpen={this.props.isOpen}
                isWithCloseButton={false}
            >
                <div
                    className="modal-question"
                    style={{ background: this.props.color }}
                >
                    {/* <div className="modal-question-overlay" /> */}
                    <div className="modal-question-header">
                        <div className="modal-question-header__left">
                            <div
                                className={`modal-question-timer font-additional${
                                    isLowTime ? " is-warning pulse-red" : ""
                                }`}
                            >
                                <FontAwesomeIcon
                                    icon={["far", "clock"]}
                                    className="icon"
                                />
                                <div>
                                    <span>{minuteLeft}</span>
                                    <span>:</span>
                                    <span>{secondLeft}</span>
                                </div>
                            </div>
                        </div>
                        <div className="modal-question-header__member">
                            <div
                                className="modal-question-member-title"
                                style={{ color: this.props.text_color }}
                            >
                                <span>{this.props.memberFor}</span>
                            </div>
                            <div
                                className="modal-question-member-value"
                                style={{ color: this.props.text_color }}
                            >
                                <span>{this.props.member}</span>
                            </div>
                        </div>
                        <div className="modal-question-header__close-button">
                            {this.props.hasMedia && (
                                <button
                                    type="button"
                                    onClick={this.handleRewatchMedia}
                                    className="btn-rewatch-media"
                                    title={
                                        this.props.introType === "text"
                                            ? i18next.t("Read Intro") ||
                                              "Read Intro"
                                            : i18next.t("Rewatch Media") ||
                                              "Rewatch Media"
                                    }
                                >
                                    <span className="rewatch-text">
                                        {this.props.introType === "text"
                                            ? i18next.t("Read Intro") ||
                                              "Read Intro"
                                            : i18next.t("Rewatch Media") ||
                                              "Rewatch Media"}
                                    </span>
                                    <div className="rewatch-icon-box">
                                        {this.props.introType === "text" ? (
                                            <FontAwesomeIcon
                                                icon={["far", "book-open"]}
                                                style={{
                                                    fontSize: "11px",
                                                    color: "#10069F",
                                                }}
                                            />
                                        ) : (
                                            <svg
                                                width="10"
                                                height="12"
                                                viewBox="0 0 12 14"
                                                fill="none"
                                            >
                                                <path
                                                    d="M11 7L1 13V1L11 7Z"
                                                    fill="#10069F"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="modal-question-body">
                        <div className="modal-question-body__main">
                            <h4
                                className="modal-question-body__main-title"
                                dangerouslySetInnerHTML={{
                                    __html: this.props.title,
                                }}
                                style={{ color: this.props.text_color }}
                            />

                            <div
                                className="modal-question-body__main-content"
                                dangerouslySetInnerHTML={{
                                    __html: this.props.question,
                                }}
                                style={{ color: this.props.text_color }}
                            />

                            {this.props.hasScale &&
                                (!this.props.scaleType ||
                                    this.props.scaleType === "likert") && (
                                    <LikertScale
                                        value={this.state.likertValue}
                                        onChange={(val) => {
                                            this.setState({ likertValue: val });
                                            if (this.props.onLikertChange) {
                                                this.props.onLikertChange(val);
                                            }
                                            this.props.onSubmitScaleAnswer?.({
                                                answer: val,
                                            });
                                        }}
                                        textColor={this.props.text_color}
                                        leftLabel={
                                            this.props.scaleLabels
                                                ?.min_value_label ||
                                            "Strongly disagree"
                                        }
                                        middleLabel={
                                            this.props.scaleLabels
                                                ?.mid_value_label ||
                                            this.props.scaleLabels
                                                ?.middle_value_label ||
                                            ""
                                        }
                                        rightLabel={
                                            this.props.scaleLabels
                                                ?.max_value_label ||
                                            "Strongly Agree"
                                        }
                                    />
                                )}

                            {this.props.hasScale &&
                                this.props.scaleType === "percentage" && (
                                    <PercentageScale
                                        value={this.state.percentageValue}
                                        onChange={(val) => {
                                            this.setState({
                                                percentageValue: val,
                                            });
                                            if (this.props.onPercentageChange) {
                                                this.props.onPercentageChange(
                                                    val,
                                                );
                                            }
                                            this.props.onSubmitScaleAnswer?.({
                                                answer: val,
                                            });
                                        }}
                                        textColor={this.props.text_color}
                                        leftLabel={
                                            this.props.scaleLabels
                                                ?.min_value_label || "0%"
                                        }
                                        rightLabel={
                                            this.props.scaleLabels
                                                ?.max_value_label || "100%"
                                        }
                                    />
                                )}

                            {(() => {
                                const inputGuiding =
                                    this.props.additionalGuidingQuestions ||
                                    this.props.additionalGuidingQuestion;
                                let rawGuiding = [];

                                const extractString = (val) => {
                                    if (typeof val === "string") return val;
                                    if (
                                        typeof val === "object" &&
                                        val !== null
                                    ) {
                                        return (
                                            val.additional_guiding_question ||
                                            val.question ||
                                            val.text ||
                                            val.value ||
                                            ""
                                        );
                                    }
                                    return "";
                                };

                                const splitTextLines = (text) => {
                                    const trimmed = (text || "").trim();
                                    if (!trimmed) return [];
                                    if (/<(?:p|li|br\s*\/?)>/i.test(trimmed)) {
                                        return trimmed
                                            .split(
                                                /<\/(?:p|li)>\s*|<br\s*\/?>\s*/i,
                                            )
                                            .map((l) =>
                                                l
                                                    .replace(/<[^>]*>/g, "")
                                                    .trim(),
                                            )
                                            .filter((l) => l.length > 0);
                                    }
                                    return trimmed
                                        .split(/\r?\n+/)
                                        .map((l) => l.trim())
                                        .filter((l) => l.length > 0);
                                };

                                if (inputGuiding) {
                                    if (typeof inputGuiding === "string") {
                                        rawGuiding =
                                            splitTextLines(inputGuiding);
                                    } else if (Array.isArray(inputGuiding)) {
                                        rawGuiding = inputGuiding.flatMap(
                                            (item) => {
                                                const str = extractString(item);
                                                return splitTextLines(str);
                                            },
                                        );
                                    } else if (
                                        typeof inputGuiding === "object"
                                    ) {
                                        const values =
                                            Object.values(inputGuiding);
                                        rawGuiding = values.flatMap((item) => {
                                            const str = extractString(item);
                                            return splitTextLines(str);
                                        });
                                    }
                                } else if (
                                    this.props.guidingQuestions &&
                                    this.props.guidingQuestions.length > 0
                                ) {
                                    rawGuiding = this.props.guidingQuestions;
                                } else if (
                                    this.props.guidingQuestion1 ||
                                    this.props.guidingQuestion2
                                ) {
                                    rawGuiding = [
                                        this.props.guidingQuestion1,
                                        this.props.guidingQuestion2,
                                    ];
                                }

                                const guidingQuestions = rawGuiding.filter(
                                    (q) =>
                                        typeof q === "string" &&
                                        q.trim().length > 0 &&
                                        q.replace(/<[^>]*>/g, "").trim()
                                            .length > 0,
                                );

                                if (guidingQuestions.length === 0) return null;

                                return (
                                    <div className="modal-question-body__guiding-wrapper">
                                        <button
                                            type="button"
                                            className={`modal-question-body__guiding-toggle ${
                                                this.state
                                                    .isOpenGuidingQuestions
                                                    ? "is-open"
                                                    : ""
                                            }`}
                                            onClick={
                                                this.toggleGuidingQuestions
                                            }
                                            style={{
                                                color:
                                                    this.props.text_color ||
                                                    "#ffffff",
                                            }}
                                            aria-expanded={
                                                this.state
                                                    .isOpenGuidingQuestions
                                            }
                                        >
                                            <span className="modal-question-body__guiding-toggle-icon">
                                                <svg
                                                    width="12"
                                                    height="8"
                                                    viewBox="0 0 12 8"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M1 1.5L6 6.5L11 1.5"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </span>
                                            <span className="modal-question-body__guiding-toggle-text">
                                                {
                                                    window.question
                                                        .follow_up_question
                                                }
                                            </span>
                                        </button>

                                        <AnimateHeight
                                            duration={300}
                                            height={
                                                this.state
                                                    .isOpenGuidingQuestions
                                                    ? "auto"
                                                    : 0
                                            }
                                            className="modal-question-body__guiding-collapse"
                                        >
                                            <div className="modal-question-body__guiding-list">
                                                {guidingQuestions.map(
                                                    (gq, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="modal-question-body__guiding-item"
                                                        >
                                                            {guidingQuestions.length >
                                                                1 && (
                                                                <span
                                                                    className="modal-question-body__guiding-bullet"
                                                                    aria-hidden="true"
                                                                />
                                                            )}
                                                            <div
                                                                className="modal-question-body__guiding-text"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: gq.replace(
                                                                        /^([•\-\*]|\d+[\.\)])\s*/,
                                                                        "",
                                                                    ),
                                                                }}
                                                                style={{
                                                                    color: this
                                                                        .props
                                                                        .text_color,
                                                                }}
                                                            />
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        </AnimateHeight>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                    <div
                        className={
                            this.state.isOpenTeamAction
                                ? "modal-question-footer is-open"
                                : "modal-question-footer"
                        }
                    >
                        <div
                            className={
                                this.state.isOpenTeamAction
                                    ? "modal-question-footer__left is-open"
                                    : "modal-question-footer__left"
                            }
                            onClick={() => this.toggleTeamAction()}
                        >
                            <div className="modal-question-footer__left-button">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                >
                                    <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z" />
                                </svg>
                            </div>
                            <div
                                className="modal-question-footer__left-text"
                                style={{ color: this.props.text_color }}
                            >
                                <p>{this.state.popup_text}</p>
                            </div>
                        </div>
                        <div
                            className={
                                this.state.isOpenTeamAction
                                    ? "modal-question-footer__right is-open"
                                    : "modal-question-footer__right"
                            }
                            role="button"
                            tabIndex={0}
                            onClick={() => this.handleClose()}
                        >
                            <div className="modal-question-footer__right-button">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                >
                                    <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
                                </svg>
                            </div>
                            <div className="modal-question-footer__right-text">
                                <p>{window.question.back_to_wheel}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className={
                        this.state.isOpenTeamAction
                            ? "team-action-container is-open"
                            : "team-action-container"
                    }
                >
                    <div className="team-action-connector-triangle" />

                    <div className="team-action">
                        <div className="team-action-header">
                            <p className="team-action-header-text">
                                {this.state.popup_text}
                            </p>
                        </div>
                        <form onSubmit={this.onSubmitTeamAction}>
                            <div
                                className={`modal-question-body__team-action ${
                                    this.state.isSubmitted ? "isSubmitted" : ""
                                }`}
                            >
                                <button
                                    type="button"
                                    className="team-action-add-new-btn"
                                    onClick={this.handleAddNewTeamAction}
                                    tabIndex={this.state.isSubmitted ? 0 : -1}
                                    disabled={!this.state.isSubmitted}
                                    title={
                                        i18next.t("Add new team action") ||
                                        "Add new team action"
                                    }
                                >
                                    <svg
                                        width="11"
                                        height="11"
                                        viewBox="0 0 11 11"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="add-new-icon"
                                    >
                                        <path
                                            d="M5.5 1.5V9.5M1.5 5.5H9.5"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <span>
                                        {i18next.t("Add new team action") ||
                                            "Add new team action"}
                                    </span>
                                </button>
                                <span className="team-action-added-text">
                                    {addedFeedbackText}
                                </span>
                                <div
                                    className={`modal-question-body__team-action_input ${
                                        this.state.isSubmitted
                                            ? "isSubmitted"
                                            : ""
                                    }`}
                                >
                                    <textarea
                                        ref={this.teamActionField}
                                        className="input-action"
                                        value={this.state.teamAction}
                                        onChange={(e) =>
                                            this.inputTeamAction(e)
                                        }
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === "Enter" &&
                                                !e.shiftKey
                                            ) {
                                                e.preventDefault();
                                                if (
                                                    this.state.teamAction &&
                                                    this.state.teamAction.trim()
                                                        .length > 0 &&
                                                    !this.state.isSubmitted &&
                                                    !this.state.isDisabled
                                                ) {
                                                    this.onSubmitTeamAction(e);
                                                }
                                            }
                                        }}
                                        placeholder={
                                            window.team_action
                                                ?.team_action_placeholder_text
                                        }
                                        disabled={
                                            this.state.isSubmitted ||
                                            this.state.isDisabled
                                        }
                                        maxLength={300}
                                        rows={1}
                                    />
                                </div>

                                <div
                                    className={`modal-question-body__cta ${
                                        this.state.teamAction.length !== 0
                                            ? "active"
                                            : ""
                                    } ${this.state.isSubmitted ? "isSubmitted" : ""}`}
                                >
                                    <Button
                                        onClick={
                                            this.state.isSubmitted
                                                ? this.handleEdit
                                                : this.onSubmitTeamAction
                                        }
                                        text={
                                            this.state.isSubmitted
                                                ? this.state.editTeamActionText
                                                : this.state.addTeamActionText
                                        }
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </Modal>
        );
    }
}

export default ModalQuestion;
